import type { AxiosPromise, AxiosResponse } from 'axios';
import type { ApiErrorDebug, ApiResponse, ErrorResponse } from './responses';
import axios from 'axios';
import { env } from '@/config/env';

/** The bos-api error envelope: `{ error, code?, correlationId?, debug? }`. */
type ApiErrorBody =
    | {
          message?: string;
          error?: string;
          errors?: Record<string, unknown>;
          code?: string;
          correlationId?: string;
          debug?: ApiErrorDebug;
      }
    | undefined;

/**
 * The correlation id, preferring the body and falling back to the response
 * header. bos-api sends both on a 500; a proxy that never reached the app can
 * leave the body unreadable while the header survives.
 */
const readCorrelationId = (
    response: AxiosResponse<ApiErrorBody> | undefined,
): string | undefined => {
    const fromBody = response?.data?.correlationId;
    if (fromBody) {
        return fromBody;
    }
    const fromHeader = response?.headers?.['x-correlation-id'];
    return typeof fromHeader === 'string' && fromHeader
        ? fromHeader
        : undefined;
};

/**
 * The `Retry-After` delay in whole seconds. bos-api sends delta-seconds; an
 * HTTP-date (which a proxy may send instead) is converted from now. Anything
 * else is ignored rather than guessed at.
 */
const readRetryAfterSeconds = (
    response: AxiosResponse<ApiErrorBody> | undefined,
): number | undefined => {
    const header = response?.headers?.['retry-after'];
    if (typeof header !== 'string' || !header.trim()) {
        return undefined;
    }
    const value = header.trim();
    if (/^\d+$/.test(value)) {
        return Number(value);
    }
    // An HTTP-date names its weekday and month; a bare "-5" is not one, though
    // Date.parse would read it as a year.
    const at = /[a-z]/i.test(value) ? Date.parse(value) : Number.NaN;
    return Number.isNaN(at)
        ? undefined
        : Math.max(0, Math.ceil((at - Date.now()) / 1000));
};

/**
 * A development build logs every server exception the API explains, so a 500
 * is visible even where the screen shows only a short message, such as a POS
 * dialog (SEL-66).
 */
const logServerException = (
    status: number,
    debug: ApiErrorDebug,
    correlationId: string | undefined,
) => {
    if (env.isDevelopmentBuild) {
        console.error(
            `bos-api ${status} (${debug.mode}, correlation ${correlationId ?? 'none'}): ` +
                `${debug.type}: ${debug.message}
${debug.stackTrace}`,
            debug.innerExceptions,
        );
    }
};

/**
 * The app's error shape for a response the server did answer. `code` and
 * `correlationId` are spread conditionally, so an absent field stays absent
 * rather than becoming an explicit `undefined`.
 */
const toErrorResponse = (
    response: AxiosResponse<ApiErrorBody>,
    extra: { isRetryable?: boolean } = {},
): ErrorResponse => {
    // bos-api error envelopes carry the text as `error`; older endpoints use
    // `message`. Accept both.
    const body = response.data;
    const message = body?.message ?? body?.error ?? response.statusText ?? '';
    const correlationId = readCorrelationId(response);
    const retryAfterSeconds = readRetryAfterSeconds(response);
    const debug = body?.debug;

    if (debug) {
        logServerException(response.status, debug, correlationId);
    }

    return {
        isAxiosError: false,
        status: response.status,
        message,
        errors: body?.errors,
        name: response.status.toString(),
        ...(body?.code ? { code: body.code } : {}),
        ...(correlationId ? { correlationId } : {}),
        ...(retryAfterSeconds !== undefined ? { retryAfterSeconds } : {}),
        ...(debug ? { debug } : {}),
        ...(extra.isRetryable ? { isRetryable: true } : {}),
    };
};

export const makeApiRequest = async <T>(
    httpPromise: AxiosPromise,
): Promise<ApiResponse<T>> => {
    let result: ApiResponse<T>;

    try {
        const response = await httpPromise;

        if (
            response.status === 200 ||
            response.status === 204 ||
            response.status === 201
        ) {
            const body = response.data as T;
            result = {
                success: true,
                response: body,
            };
        } else {
            result = {
                success: false,
                error: toErrorResponse(response as AxiosResponse<ApiErrorBody>),
            };
        }
    } catch (err) {
        const error = err as {
            response?: AxiosResponse<ApiErrorBody>;
            isRetryable?: boolean;
        };
        // Set by the auth interceptor when a session refresh hit temporary
        // trouble (429, 5xx, network): the session is kept, so retrying works.
        const isRetryable = error?.isRetryable === true;

        if (error.response) {
            result = {
                success: false,
                error: toErrorResponse(error.response, { isRetryable }),
            };
        } else if (axios.isCancel(error)) {
            result = {
                success: false,
                error: {
                    isAxiosError: true,
                    status: 499,
                    message: 'Request cancelled',
                    name: '499',
                },
            };
        } else {
            result = {
                success: false,
                error: {
                    isAxiosError: true,
                    status: 500,
                    message: 'ERR_CONNECTION_REFUSED. Possible network problem',
                    name: '500',
                    ...(isRetryable && { isRetryable }),
                },
            };
        }
    }

    return result;
};
